<template>
    <two-column-layout>
        <card-container>
            <h2>{{ $t(i18n.title) }}</h2>
            <p>{{ $t(i18n.description) }}</p>

            <filterable-drop-down-fragment
                v-if="!isObjectEmpty(inviteType)"
                :label="$t(i18n.invite_type.label)"
                :placeholder="$t(i18n.invite_type.placeholder)"
                v-model="inviteType"
                :is-invalid="isFieldInvalid($v.inviteType)"
                :options="inviteTypes"
                size="max"
                show-info
                @info-clicked="onInfoClicked('GENERIC_INVITE_INVITE_TYPE')"
            >
                {{ $t(i18n.invite_type.description) }}
                <template #result>
                    <field-error-messages :validation-state="$v.inviteType" />
                </template>
            </filterable-drop-down-fragment>

            <radio-group-fragment
                key="select-invitee-type"
                :label="$t(i18n.invitee_type.label)"
                :description="$t(i18n.invitee_type.description)"
                v-model="inviteeType"
                :is-invalid="isFieldInvalid($v.inviteeType)"
                group-name="group-invitee-type"
                :options="inviteeTypeOptions"
            >
                <template #result>
                    <field-error-messages :validation-state="$v.inviteeType"/>
                </template>
            </radio-group-fragment>

            <fragment-separator />

            <text-input-fragment
                key="cocId"
                :disabled="validatingCoc"
                :label="$t(i18n.coc.id.label)"
                v-model="cocId"
                :is-invalid="isFieldInvalid($v.cocId) || isFieldInvalid($v.cocValidation)"
                @input="setCocId"
                margin-top="0"
            >
                <template #result>
                    <field-error-messages :validation-state="$v.cocId" />
                </template>
            </text-input-fragment>

            <text-input-fragment
                key="cocSubId"
                :disabled="validatingCoc"
                :label="$t(i18n.coc.sub_id.label)"
                v-model="cocSubId"
                :is-invalid="isFieldInvalid($v.cocSubId) || isFieldInvalid($v.cocValidation)"
                @input="setCocSubId"
            >
                <template #result v-if="!validatingCoc">
                    <p v-if="isCocValid">
                        <br />{{ companyData.companyName }}<br />
                        {{ companyData.companyStreetName }}
                        {{ companyData.companyHouseNumber }} <br />
                        {{ companyData.companyPostalCode }}
                        {{ companyData.companyCityName }}
                    </p>
                    <field-error-messages :validation-state="$v.cocSubId" />
                    <field-error-messages :validation-state="$v.cocValidation.data" />
                </template>
            </text-input-fragment>

            <fragment-separator />

            <text-input-fragment
                key="firstName"
                :label="$t(i18n.first_name.label)"
                v-model="firstName"
                :is-invalid="isFieldInvalid($v.firstName)"
            >
                <template #result>
                    <field-error-messages :validation-state="$v.firstName" />
                </template>
            </text-input-fragment>

            <text-input-fragment
                key="lastName"
                :label="$t(i18n.last_name.label)"
                v-model="lastName"
                :is-invalid="isFieldInvalid($v.lastName)"
            >
                <template #result>
                    <field-error-messages :validation-state="$v.lastName" />
                </template>
            </text-input-fragment>

            <text-input-fragment
                key="email"
                :label="$t(i18n.email.label)"
                v-model="email"
                :is-invalid="isFieldInvalid($v.email)"
            >
                <template #result>
                    <field-error-messages :validation-state="$v.email" />
                </template>
            </text-input-fragment>

            <button-group>
                <button-fragment
                    :disabled="finalizing"
                    @click="submit()"
                >
                    {{$t(i18n.submission.label)}}
                </button-fragment>
            </button-group>
        </card-container>
    </two-column-layout>
</template>

<script>

import { cocApi } from "../../api/coc-api";
import {
    ButtonGroup,
    FragmentSeparator,
    ButtonFragment,
    TwoColumnLayout,
    CardContainer,
    TextInputFragment,
    RadioGroupFragment,
    FilterableDropDownFragment
} from "@bvm/forms";
import FieldErrorMessages from "../shared/field-error-messages";
import MediaItemModal from "../shared/media-item-modal";
import { Utils } from "@bvm/core";
import { finalizeForm, getInviteTypes } from "./api/generic-invite-api";
import {
    email,
    maxLength,
    minLength,
    numeric,
    required,
    requiredIf
} from "vuelidate/lib/validators";

const i18nBuilder = new Utils.I18n.I18nBuilder("generic_invite")
    .add("title")
    .add("description")
    .add("invite_type", b => b.add("label")
        .add("placeholder"))
    .add("invitee_type", b => b.add("label")
        .add("description")
        .add("customer")
        .add("prospect"))
    .add("coc", b => b.add("id",
            c => c.add("label"))
        .add("sub_id",
            c => c.add("label")))
    .add("first_name", b => b.add("label"))
    .add("last_name", b => b.add("label"))
    .add("email", b => b.add("label"))
    .add("submission", b => b.add("label").add("success").add("failure"))
    .add("invite_type_fetch_failure");

const i18nValidationBuilder = new Utils.I18n.I18nBuilder("validations_field")
    .add("email")
    .add("required")
    .add("coc")
    .add("NO_COMPANY_FOUND")
    .add("TOO_MANY_REQUESTS")

export const messageKeys = [
    ...i18nBuilder.getKeys(),
    ...i18nValidationBuilder.getKeys(),
];

export default {
    name: "generic-invite-form",
    components: {
        RadioGroupFragment,
        FragmentSeparator,
        TextInputFragment,
        CardContainer,
        TwoColumnLayout,
        FieldErrorMessages,
        ButtonGroup,
        ButtonFragment,
        FilterableDropDownFragment
    },
    data() {
        return {
            i18n: i18nBuilder.getData(),
            inviteeType: "",
            inviteType: null,
            inviteTypes: [],
            cocId: "",
            cocSubId: "",
            cocValidation: "",
            companyData: {
                companyName: "",
                companyStreetName: "",
                companyHouseNumber: "",
                companyPostalCode: "",
                companyCityName: ""
            },
            firstName: "",
            lastName: "",
            email: "",
            validatingCoc: false,
            finalizing: false
        }
    },
    methods: {
        setCocId(value) {
            this.cocId = value;
            this.validateCocExternally();
        },
        setCocSubId(value) {
            this.cocSubId = value;
            this.validateCocExternally();
        },
        validateCocExternally() {
            if (
                !this.$v.cocId.$invalid &&
                !this.$v.cocSubId.$invalid
            ) {
                this.getCompanyRdcData();
            }
        },
        getCompanyRdcData() {
            this.validatingCoc = true;
            this.companyData = {};
            cocApi(
                this.cocId,
                this.cocSubId,
                "INCOME_INSURANCE_INVITE"
            ).then(result => {
                    this.cocValidation = result;
                    if (this.cocValidation.data) {
                        this.companyData = {
                            companyName: result.data.name,
                            companyStreetName: result.data.organizationAddress.streetName,
                            companyHouseNumber:
                                result.data.organizationAddress.houseNumber +
                                (result.data.organizationAddress.houseNumberAddition || ""),
                            companyPostalCode: result.data.organizationAddress.postalCode,
                            companyCityName: (result.data.organizationAddress.city || "").toLowerCase()
                        };
                    }
                }
            ).finally(() => {
                this.$v.cocId.$touch();
                this.$v.cocSubId.$touch();
                this.$v.cocValidation.$touch();
                this.validatingCoc = false;
            });
        },
        isFieldInvalid(field) {
            return field.$dirty && field.$invalid;
        },
        isObjectEmpty(obj) {
            return obj && Object.keys(obj).length === 0;
        },
        onInfoClicked(mediaItemTag) {
            this.$bvmModal.show(MediaItemModal, {
                itemTag: mediaItemTag
            }, {
                clickToClose: true
            });
        },
        submit() {
            this.$v.$touch();
            if (this.$v.$invalid || this.finalizing) {
                return;
            }

            this.finalizing = true;

            const postData = {
                prospect: this.inviteeType === 'prospect',
                inviteType: this.inviteType,
                companyName: this.companyData.companyName,
                cocId: this.cocId,
                cocSubId: this.cocSubId,
                firstName: this.firstName,
                lastName: this.lastName,
                email: this.email
            }

            finalizeForm(postData)
                .then(() => {
                    Bovemij.notifySuccess(this.$t(this.i18n.submission.success));
                    this.resetState();
                })
                .catch(() => Bovemij.notifyFailure('',this.$t(this.i18n.submission.failure)))
                .finally(() => this.finalizing = false);
        },
        resetState() {
            this.inviteeType = "";
            this.inviteType = "";
            this.companyData = {};
            this.cocId = "";
            this.cocSubId = "";
            this.firstName = "";
            this.lastName = "";
            this.email = "";
            this.$v.$reset();
        }
    },
    computed: {
        inviteeTypeOptions() {
            return {
                customer: this.$t(this.i18n.invitee_type.customer),
                prospect: this.$t(this.i18n.invitee_type.prospect)
            };
        },
        isCocValid() {
            return (
                !this.$v.cocId.$invalid &&
                !this.$v.cocSubId.$invalid &&
                !this.$v.cocValidation.$invalid &&
                this.companyData.companyName
            );
        }
    },
    created() {
        getInviteTypes()
            .then(fetchedInviteTypes => {
                for (const fetchedInviteType of fetchedInviteTypes) {
                    this.inviteTypes.push({value:fetchedInviteType.inviteType, label:fetchedInviteType.label});
                }
            }).catch(() => Bovemij.notifyFailure('',this.$t(this.i18n.invite_type_fetch_failure)));

        this.setCocId(Bovemij.CurrentProfile.organization.cocId);
        this.setCocSubId(Bovemij.CurrentProfile.organization.cocSubId);
    },
    validations() {
        return {
            inviteeType: { required },
            inviteType: { required },
            cocId: {
                required,
                numeric,
                minLength: minLength(8),
                maxLength: maxLength(8)
            },
            cocSubId: {
                required,
                numeric,
                minLength: minLength(12),
                maxLength: maxLength(12)
            },
            companyData: {
                companyName: { required }
            },
            firstName: { required },
            lastName: { required },
            email: {
                required,
                email
            },
            cocValidation: {
                data: {
                    ["coc_" + this.cocValidation.errorCode]: requiredIf(
                        () => !this.$v.cocId.$invalid && !this.$v.cocSubId.$invalid
                    )
                }
            }
        }
    }
}
</script>